import React, { useState } from 'react';
import { NCZIEntry } from '../../../Interfaces/ncziTypes';
import useNCZICommandProcess from '../../../Hooks/useNCZICommandProcess';
import { useEffectAsync } from '../../../../../helpers';
import { GetPatientDPN } from '../../../Commands/GetPatientDPN';
import NCZICommandResult from '../../NCZICommandResult/NCZICommandResult';
import Loading from '../../../../../Components/Loading';
import NZCIListItem from '../../Base/NZCIListItem';
import FormattedDate from '../../../../../Components/FormattedDate';
import Tag from '../../../../../Components/Tag/Tag';
import EHREntryInfo from '../../EHR/EHRExtract/EHREntry/EHREntryInfo';
import Text from '../../../../../Components/Base/Text/Text';
import useTranslation from '../../../../../Hooks/useTranslation';
import moment from 'moment-timezone';
import ElectronicDPNChangeModal from '../Change/ElectronicDPNChangeModal';
import LinkButton from '../../../../../Components/Button/LinkButton';
import { Theme } from '../../../../../Enums/Theme.enum';
import ElectronicDPNCancellationModal from '../Cancellation/ElectronicDPNCancellationModal';
import { ElectronicDPNCancellationValues } from '../Cancellation/ElectronicDPNCancellation';
import { CancelDPN } from '../../../Commands/CancelDPN';
import useNotification from '../../../../../Hooks/useNotification.hook';

// @ts-ignore
import styles from './ElectronicDPNDetailModalContent.module.scss';
import ElectronicDPNCreateModal from '../Create/ElectronicDPNCreateModal';

interface Props {
	onClose: (needReload: boolean) => void;
	patientIdentifier: string;
	electronicConfirmation: NCZIEntry;
	readonly: boolean;
	disableCreate?: boolean;
}

const ElectronicDPNDetailModalContent: React.FC<Props> = props => {
	const { patientIdentifier, electronicConfirmation, onClose, readonly = false, disableCreate = false } = props;
	const [confirmationDetail, setConfirmationDetail] = useState(null);
	const {t} = useTranslation();
	const [electronicDPNChangeOpen, setElectronicDPNChangeOpen] = useState<boolean>(false);
	const [electronicDPNCancellationOpen, setElectronicDPNCancellationOpen] = useState<boolean>(false);
	const [electronicDPNContinueOpen, setElectronicDPNContinueOpen] = useState<boolean>(false);
	const [cancelling, setCancelling] = useState<boolean>(false);
	const { showNotificationSuccess } = useNotification();

	const { processingCommandId, processCommand, isProcessing } =
		useNCZICommandProcess({
			onFinish: (commandId, result) => {
				if(!cancelling) {
					setConfirmationDetail(result.data);
				} else {
					showNotificationSuccess({
						content: t('nczi.result.updateSuccess')
					});
					onClose(true);
				}
			},
			hideSuccess: true
		});

	const handleCloseElectronicDPNChangeModal = (
		needReload: boolean = false,
	) => {
		setElectronicDPNChangeOpen(false);

		if(needReload) {
			onClose?.(needReload);
		}
	};

	const handleCloseContinueDPN = (
		needReload: boolean = false,
	) => {
		setElectronicDPNContinueOpen(false);

		if(needReload) {
			onClose?.(needReload);
		}
	};

	const handleCancelDPN = async (values: ElectronicDPNCancellationValues) => {
		const stornoCommand = CancelDPN({
			patientCode: patientIdentifier,
			confirmation: {
				previousVersion: confirmationDetail.previousVersion,
				versionSetId: confirmationDetail.versionSetId || undefined,
				versionStatus: 'VER04',
				sensitivity: 3,
				createdAt: moment().format(),
				uncertaintyExpressed: false,
				reason: values.reason,
			},
		});

		await processCommand(stornoCommand);
		setCancelling(true);
	}

	useEffectAsync(async () => {
		const GetDetailCommand = GetPatientDPN({
			patientCode: patientIdentifier,
			confirmationId: electronicConfirmation.recordId!
		});

		await processCommand(GetDetailCommand);
	}, []);

	if (isProcessing && processingCommandId) {
		return <NCZICommandResult commandId={processingCommandId} />;
	}

	if(confirmationDetail === null) {
		return <Loading></Loading>;
	}

	const isCancelled = confirmationDetail.versionStatus === 'VER04';
	let alcoholConsumption = 'base.shared.notSpecified';
	if(confirmationDetail.incapableReason.alcoholConsumption !== null) {
		alcoholConsumption = confirmationDetail.incapableReason.alcoholConsumption ? 'base.yes' : 'base.no';
	}

	return (
		<>
			<NZCIListItem title={'nczi.electronicDPN.detail.confirmationId'} value={electronicConfirmation.recordId?.code} />
			{isCancelled && <Tag label={'nczi.cancelled'} className={styles.cancelledTag} />}
			<EHREntryInfo info={{
				time: confirmationDetail.createdAt,
				user: {
					code: confirmationDetail.committer.extension,
					scheme: {
						oid: confirmationDetail.committer.root.oid
					}
				},
				version: confirmationDetail.versionStatus
			}} />
			<br />
			<NZCIListItem title={'nczi.electronicDPN.detail.issueDate'} value={<FormattedDate date={confirmationDetail.issueDate} />} />
			<NZCIListItem title={'nczi.electronicDPN.detail.treatmentAbroad'} value={t(confirmationDetail.treatmentAbroad ? 'base.yes' : 'base.no')} />
			<NZCIListItem title={'nczi.electronicDPN.detail.changeDate'} value={<FormattedDate date={confirmationDetail.changeDate} />} />
			<br />
			<NZCIListItem title={'nczi.electronicDPN.detail.incapableFrom'} value={<FormattedDate date={confirmationDetail.incapableFrom} />} />
			{confirmationDetail.capableFrom && <NZCIListItem title={'nczi.electronicDPN.detail.capableFrom'} value={<FormattedDate date={confirmationDetail.capableFrom} />} />}
			{!confirmationDetail.capableFrom && <NZCIListItem title={'nczi.electronicDPN.detail.capableFrom'} value={<strong><Text tx={'nczi.electronicDPN.withoutEnd'} /></strong>} />}
			<br />
			<NZCIListItem title={'nczi.electronicDPN.detail.note'} value={confirmationDetail.note || '-'} />
			<br />

			<Text tx={'nczi.electronicDPN.detail.incapableDuration.title'} firstUpper />
			<div className={styles.child}>
				<NZCIListItem title={'nczi.electronicDPN.detail.incapableDuration.anticipatedTerminationDate'} value={<FormattedDate date={confirmationDetail.incapableDuration.anticipatedTerminationDate} />} />
				<NZCIListItem title={'nczi.electronicDPN.detail.incapableDuration.note'} value={confirmationDetail.incapableDuration.note || '-'} />
			</div>

			<Text tx={'nczi.electronicDPN.detail.incapableReason.title'} firstUpper />
			<div className={styles.child}>
				<NZCIListItem title={'nczi.electronicDPN.detail.incapableReason.reason'} value={confirmationDetail.incapableReason.reason?.displayName} />
				<NZCIListItem title={'nczi.electronicDPN.detail.incapableReason.alcoholConsumption'} value={t(alcoholConsumption)} />
				<NZCIListItem title={'nczi.electronicDPN.detail.incapableReason.validFrom'} value={confirmationDetail.incapableReason.validFrom ? <FormattedDate date={confirmationDetail.incapableReason.validFrom} /> : '-'} />
			</div>

			<Text tx={'nczi.electronicDPN.detail.diagnosis.title'} firstUpper />
			<div className={styles.child}>
				<NZCIListItem title={'nczi.electronicDPN.detail.diagnosis.code'} value={confirmationDetail.diagnosis.code?.displayName} />
				<NZCIListItem title={'nczi.electronicDPN.detail.diagnosis.validFrom'} value={confirmationDetail.diagnosis.validFrom ? <FormattedDate date={confirmationDetail.diagnosis.validFrom} /> : '-'} />
			</div>

			<Text tx={'nczi.electronicDPN.detail.placeOfStay.title'} firstUpper />
			<div className={styles.child}>
				<NZCIListItem title={'nczi.electronicDPN.detail.placeOfStay.floor'} value={confirmationDetail.placeOfStay.floor || '-'} />
				<NZCIListItem title={'nczi.electronicDPN.detail.placeOfStay.apartmentNumber'} value={confirmationDetail.placeOfStay.apartmentNumber || '-'} />
				<NZCIListItem title={'nczi.electronicDPN.detail.placeOfStay.note'} value={confirmationDetail.placeOfStay.note || '-'} />
				<NZCIListItem title={'nczi.electronicDPN.detail.placeOfStay.address.title'} value={''} />
				<div className={styles.child}>
					<NZCIListItem title={'nczi.electronicDPN.detail.placeOfStay.address.street'} value={confirmationDetail.placeOfStay.address.street} />
					<NZCIListItem title={'nczi.electronicDPN.detail.placeOfStay.address.descriptiveNumber'} value={confirmationDetail.placeOfStay.address.descriptiveNumber} />
					<NZCIListItem title={'nczi.electronicDPN.detail.placeOfStay.address.guideNumber'} value={confirmationDetail.placeOfStay.address.guideNumber} />
					<NZCIListItem title={'nczi.electronicDPN.detail.placeOfStay.address.city'} value={confirmationDetail.placeOfStay.address.city} />
					<NZCIListItem title={'nczi.electronicDPN.detail.placeOfStay.address.country'} value={confirmationDetail.placeOfStay.address.country} />
					<NZCIListItem title={'nczi.electronicDPN.detail.placeOfStay.address.postCode'} value={confirmationDetail.placeOfStay.address.postCode} />
				</div>
			</div>

			<NZCIListItem title={'nczi.electronicDPN.detail.phone'} value={confirmationDetail.phone || '-'} />

			{confirmationDetail.outings.allowedOutings.length > 0 && <>
				<br />
				<Text tx={'nczi.electronicDPN.detail.outings.title'} firstUpper />
				<div className={styles.child}>
					{confirmationDetail.outings.allowedOutings.map(outing => <>
						<NZCIListItem title={'nczi.electronicDPN.create.outings.dateFrom'} value={moment(outing.dateFrom).format('DD.MM.YYYY')} />
						<div className={styles.child}>
							<NZCIListItem title={'nczi.electronicDPN.create.outings.timeFrom'} value={outing.timeFrom} />
							<NZCIListItem title={'nczi.electronicDPN.create.outings.timeTo'} value={outing.timeTo} />
						</div>
					</>)}
				</div>
			</>}

			<br />

			{!isCancelled && !readonly && !confirmationDetail.capableFrom && (
				<>
					<LinkButton
						firstUpper
						title={'nczi.endOrChangeDPN'}
						theme={Theme.BLUE}
						onClick={() => setElectronicDPNChangeOpen(true)}
					/>
					<br />
				</>
			)}
			{!isCancelled && confirmationDetail.capableFrom && (
				<>
					<LinkButton
						firstUpper
						title={'nczi.continueDPN'}
						theme={disableCreate ? Theme.DISABLED : Theme.BLUE}
						onClick={() => {
							if(!disableCreate) {
								setElectronicDPNContinueOpen(true)
							}
						}}
					/>
					<br />
				</>
			)}
			{!isCancelled && !readonly && <LinkButton
				title={'nczi.action.cancelDPN'}
				onClick={() =>
					setElectronicDPNCancellationOpen(true)
				}
				theme={Theme.RED}
			/>}

			<ElectronicDPNChangeModal
				opened={electronicDPNChangeOpen}
				onClose={handleCloseElectronicDPNChangeModal}
				patientIdentifier={patientIdentifier}
				electronicConfirmation={electronicConfirmation}
				preloadedData={confirmationDetail}
			/>
			<ElectronicDPNCancellationModal
				isOpen={electronicDPNCancellationOpen}
				onClose={() => setElectronicDPNCancellationOpen(false)}
				onSubmit={handleCancelDPN}
				electronicConfirmation={confirmationDetail}
			/>
			<ElectronicDPNCreateModal
				opened={electronicDPNContinueOpen}
				onClose={handleCloseContinueDPN}
				patientIdentifier={patientIdentifier}
				electronicConfirmation={confirmationDetail}
			/>
		</>
	);
};

export default ElectronicDPNDetailModalContent;
